import React, { useState, useRef } from 'react';
import { GripVertical } from 'lucide-react';
import { ActivityExpandPanel } from './ActivityExpandPanel';
import { ActivityQuickAdd } from './ActivityQuickAdd';
import type { Activity } from '@/types/activity.types';

interface ActivityListViewProps {
  activities: Activity[];
  eventId: number;
  canManage: boolean;
  onAdded: (title: string) => Promise<void>;
  onUpdated: (updated: Activity) => void;
  onDeleted: (id: number) => void;
  onReorder: (reordered: Activity[]) => Promise<void>;
}

export const ActivityListView: React.FC<ActivityListViewProps> = ({
  activities,
  eventId,
  canManage,
  onAdded,
  onUpdated,
  onDeleted,
  onReorder,
}) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const dragItem = useRef<number | null>(null);

  const toggleExpand = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // ─── Drag-and-drop reorder (HTML5 native) ──────────────────────────────────

  const handleDragStart = (e: React.DragEvent, id: number) => {
    dragItem.current = id;
    e.dataTransfer.effectAllowed = 'move';
    // Make ghost semi-transparent
    const el = e.currentTarget as HTMLElement;
    setTimeout(() => { el.style.opacity = '0.4'; }, 0);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).style.opacity = '1';
    dragItem.current = null;
    setDragOverId(null);
  };

  const handleDragOver = (e: React.DragEvent, id: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragItem.current !== id) {
      setDragOverId(id);
    }
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const handleDrop = (e: React.DragEvent, targetId: number) => {
    e.preventDefault();
    setDragOverId(null);

    const fromId = dragItem.current;
    if (!fromId || fromId === targetId) return;

    const fromIndex = activities.findIndex((a) => a.id === fromId);
    const toIndex = activities.findIndex((a) => a.id === targetId);
    if (fromIndex < 0 || toIndex < 0) return;

    const reordered = [...activities];
    const [removed] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, removed);

    // Re-number sequence_order 1-based
    const withNewSeq = reordered.map((a, i) => ({ ...a, sequence_order: i + 1 }));
    onReorder(withNewSeq);
  };

  // ─── Parallel group helpers ─────────────────────────────────────────────────

  const getGroupLabel = (activity: Activity): string => {
    if (!activity.parallel_group) return String(activity.sequence_order);
    // Find parallel siblings to build label like "3a", "3b"
    const siblings = activities.filter((a) => a.parallel_group && a.parallel_group === activity.parallel_group);
    const posInGroup = siblings.findIndex((a) => a.id === activity.id);
    const seqNum = activity.sequence_order;
    return `${seqNum}${String.fromCharCode(97 + posInGroup)}`;
  };

  // Group activities for the parallel indicator lines
  const parallelGroups = new Map<string, number[]>();
  activities.forEach((a) => {
    if (a.parallel_group) {
      if (!parallelGroups.has(a.parallel_group)) parallelGroups.set(a.parallel_group, []);
      parallelGroups.get(a.parallel_group)!.push(a.id);
    }
  });

  const isFirstInGroup = (activity: Activity): boolean => {
    if (!activity.parallel_group) return false;
    const group = parallelGroups.get(activity.parallel_group) || [];
    return group[0] === activity.id;
  };

  const isLastInGroup = (activity: Activity): boolean => {
    if (!activity.parallel_group) return false;
    const group = parallelGroups.get(activity.parallel_group) || [];
    return group[group.length - 1] === activity.id;
  };

  return (
    <div className="space-y-1.5">
      {activities.map((activity) => {
        const inGroup = Boolean(activity.parallel_group);
        const firstInGroup = isFirstInGroup(activity);
        const lastInGroup = isLastInGroup(activity);
        const isDragOver = dragOverId === activity.id;

        return (
          <div
            key={activity.id}
            className={`
              ${inGroup && firstInGroup ? 'pt-1 border-t-2 border-l-2 border-purple-200 rounded-tl-lg ml-2 pl-2' : ''}
              ${inGroup && !firstInGroup && !lastInGroup ? 'border-l-2 border-purple-200 ml-2 pl-2' : ''}
              ${inGroup && lastInGroup ? 'pb-1 border-b-2 border-l-2 border-purple-200 rounded-bl-lg ml-2 pl-2' : ''}
            `}
          >
            {inGroup && firstInGroup && (
              <div className="text-xs text-purple-500 font-medium mb-1 ml-1 flex items-center gap-1">
                <span>⫠</span>
                <span>Parallel Group: {activity.parallel_group}</span>
              </div>
            )}
            <div
              draggable={canManage}
              onDragStart={(e) => handleDragStart(e, activity.id)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, activity.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, activity.id)}
              className={`
                flex items-start gap-2 
                ${isDragOver ? 'ring-2 ring-purple-400 ring-offset-1 rounded-lg' : ''}
              `}
            >
              {/* Drag handle */}
              {canManage && (
                <div
                  className="mt-3 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 flex-shrink-0"
                  title="Drag to reorder"
                >
                  <GripVertical className="h-4 w-4" />
                </div>
              )}

              {/* Sequence label */}
              <div className="mt-3 text-xs font-bold text-gray-400 w-8 text-right flex-shrink-0">
                {getGroupLabel(activity)}
              </div>

              {/* Expand panel takes the rest */}
              <div className="flex-1 min-w-0">
                <ActivityExpandPanel
                  activity={activity}
                  eventId={eventId}
                  isOpen={expandedId === activity.id}
                  onToggle={() => toggleExpand(activity.id)}
                  onUpdated={onUpdated}
                  onDeleted={onDeleted}
                  canManage={canManage}
                />
              </div>
            </div>
          </div>
        );
      })}

      {/* Quick Add */}
      {canManage && (
        <div className="mt-2">
          <ActivityQuickAdd onAdd={onAdded} />
        </div>
      )}

      {activities.length === 0 && (
        <div className="text-center py-10 text-gray-400 text-sm">
          No activities yet. Add the first one above.
        </div>
      )}
    </div>
  );
};
